import { forwardRef, useMemo } from "react";
import { useLocation, type LinkProps as RouterLinkProps, Link as RouterLink } from "react-router-dom";

export interface LinkProps extends RouterLinkProps {
  keepSearch?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(({ keepSearch = false, to, ...rest }, ref) => {
  const location = useLocation();
  const currentSearch = location.search;

  const finalTo = useMemo(() => {
    if (!keepSearch) {
      return to;
    }

    // If to is a string, append current search params
    if (typeof to === "string") {
      // Handle relative paths, absolute paths, and URLs with existing search params
      const parts = to.split("?");
      const pathname = parts[0] ?? "";
      const existingSearch = parts[1];
      const searchParams = new URLSearchParams(currentSearch);

      // Merge existing search params from the 'to' prop (they take precedence)
      if (existingSearch) {
        const toParams = new URLSearchParams(existingSearch);
        toParams.forEach((value, key) => {
          searchParams.set(key, value);
        });
      }

      const search = searchParams.toString();
      return search ? `${pathname}?${search}` : pathname;
    }

    // If to is an object, merge search params
    return {
      ...to,
      search: (() => {
        const searchParams = new URLSearchParams(currentSearch);

        // Merge existing search params from the 'to' object (they take precedence)
        if (to.search) {
          const toParams = new URLSearchParams(to.search);
          toParams.forEach((value, key) => {
            searchParams.set(key, value);
          });
        }

        const search = searchParams.toString();
        return search ? `?${search}` : "";
      })(),
    };
  }, [keepSearch, to, currentSearch]);

  return <RouterLink to={finalTo} {...rest} ref={ref} />;
});
